import { useState } from 'preact/hooks';
import { cn } from '../lib/cn';
import { t } from '../lib/i18n';
import {
  isBoxedPreview,
  isPreviewable,
  type MediaAttachment,
} from '../client/media';
import { MessageAttachment } from './MessageAttachment';

interface MessageMediaProps {
  attachment: MediaAttachment;
}

/**
 * Cancels the bubble's own padding so media sits flush against its edges.
 * Mirrors `px-[10px] pt-[8px] pl-[12px]` in MessageBubble — if that padding
 * changes, this has to follow.
 */
const BUBBLE_BLEED = '-mt-[8px] -ml-[12px] -mr-[10px]';

/**
 * Every boxed preview renders into the same 3:2 frame, filled with
 * `object-cover`.
 *
 * The backend does not send the image's real dimensions (only name, mime and
 * byte size), so any reserved space is a guess — and a guess means the bubble
 * jumps when the bytes land. A uniform frame sidesteps that entirely:
 * placeholder and image occupy the exact same box, so nothing moves. It is
 * what WhatsApp does with its thumbnails. The cost is that a portrait photo
 * gets cropped in the preview; clicking opens the full one.
 *
 * The 420px base width is the old `max-h-[280px]` bound expressed as a
 * width: at 3:2 those are the same box. Do NOT let this grow to the bubble's
 * full width — in the standalone view that is 76% of the viewport and the
 * image swallows the screen.
 *
 * The max-width is `100% + 22px`, not `100%`, because 100% resolves against
 * the bubble's CONTENT box while the bleed cancels its 12px + 10px of
 * horizontal padding. With a plain `max-w-full` a clamped frame stopped 22px
 * short of the right edge and left a white gap there.
 *
 * Written out in full on purpose: Tailwind scans for literal class strings,
 * so composing this from a variable would silently emit no CSS at all.
 *
 * If `content.file` ever carries width/height, swap this for the real ratio
 * and the crop disappears.
 */
const PREVIEW_FRAME =
  'relative w-[420px] max-w-[calc(100%_+_22px)] aspect-[3/2] overflow-hidden';

/** Sweeping highlight — reads as "loading" far better than a flat grey block. */
function Shimmer() {
  return (
    <div
      data-testid="media-placeholder"
      aria-hidden="true"
      class="absolute inset-0 overflow-hidden bg-black/[0.06]"
    >
      <div class="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/50 to-transparent" />
    </div>
  );
}

/**
 * Decides how an attachment is shown: inline preview when the browser can
 * render it, download card otherwise.
 *
 * Unlike the inbox (`ImageMessage.tsx` in mirlo-frontend), the widget does
 * NOT fetch the bytes into a blob first. Its media URL carries its own
 * credential in the query string, so the browser can load it directly —
 * which also means native lazy loading and HTTP caching work for free.
 *
 * Any load failure degrades to the card: an expired media token, a network
 * blip or a codec the browser refuses must never leave an empty bubble.
 */
export function MessageMedia({ attachment }: MessageMediaProps) {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const { kind, name, url } = attachment;

  /**
   * A cached image can already be complete by the time the element is
   * attached, and then `load` never fires — which would leave the
   * placeholder up forever. The endpoint sends `Cache-Control: max-age=600`,
   * so this is the common path on a reopened panel, not an edge case.
   */
  const onImageRef = (el: HTMLImageElement | null) => {
    if (el?.complete && el.naturalWidth > 0) setLoaded(true);
  };

  if (failed || !isPreviewable(attachment)) {
    return <MessageAttachment attachment={attachment} />;
  }

  if (isBoxedPreview(attachment)) {
    const frame = cn(PREVIEW_FRAME, BUBBLE_BLEED);

    if (kind === 'video') {
      return (
        <div class={frame}>
          <video
            src={url}
            controls
            preload="metadata"
            onError={() => setFailed(true)}
            class="absolute inset-0 h-full w-full object-cover"
          />
        </div>
      );
    }

    return (
      <a
        href={url}
        target="_blank"
        // The URL carries a media token — keep it out of the Referer header.
        rel="noopener noreferrer"
        class={cn(frame, 'block')}
      >
        {!loaded && <Shimmer />}
        <img
          ref={onImageRef}
          src={url}
          // Never an empty alt: on a caption-less message the image IS the
          // content, so a screen reader must still announce something.
          alt={name ?? t('attachment')}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          class={cn(
            'absolute inset-0 h-full w-full object-cover',
            'transition-opacity duration-200',
            loaded ? 'opacity-100' : 'opacity-0',
          )}
        />
      </a>
    );
  }

  // Audio is a thin native player — no frame to reserve, no placeholder.
  return (
    <audio
      src={url}
      controls
      preload="metadata"
      onError={() => setFailed(true)}
      class="block w-full max-w-[260px]"
    />
  );
}
